import type { JurisdictionAvailability, OverallStatus, RequirementStatus, ResponsibleParty, TaxEffect, TaxSetupRequirement } from './types';

export const REQUIREMENT_STATUS_LABEL: Record<RequirementStatus, string> = {
  Complete: 'Complete',
  NeedsInformation: 'Needs information',
  NeedsReview: 'Needs review',
  NotApplicable: 'Not applicable',
  Unsupported: 'Not supported yet',
  Expired: 'Expired',
  EffectiveInFuture: 'Starts later',
};

/** A short mark that goes with the words, so a status is never conveyed by colour alone. */
export const REQUIREMENT_STATUS_ICON: Record<RequirementStatus, string> = {
  Complete: '✓',
  NeedsInformation: '!',
  NeedsReview: '?',
  NotApplicable: '–',
  Unsupported: '×',
  Expired: '!',
  EffectiveInFuture: '…',
};

export const PARTY_LABEL: Record<ResponsibleParty, string> = {
  Employee: 'The employee',
  Employer: 'Your company',
  PayrollAdmin: 'Your payroll administrator',
  TaxReviewer: 'A payroll tax reviewer',
  EPN: 'EPN',
};

export const EFFECT_LABEL: Record<TaxEffect, string> = { Calculation: 'Calculation', Payment: 'Payment', Filing: 'Filing' };

export const AVAILABILITY_LABEL: Record<JurisdictionAvailability, string> = {
  Certified: 'Certified',
  PilotOnly: 'Pilot only',
  ConfigurationAvailable: 'Configuration available',
  CalculationNotYetCertified: 'Calculation not yet certified',
  FilingNotYetAvailable: 'Filing not yet available',
  Unsupported: 'Unsupported',
};

/** Worst first: the order that decides the overall status of a setup and of a checklist row. NotApplicable never takes part. */
export const STATUS_PRECEDENCE: readonly OverallStatus[] = ['Unsupported', 'Expired', 'NeedsInformation', 'NeedsReview', 'EffectiveInFuture', 'Complete'];

/** The worst status among the applicable requirements, or NotApplicable when none applies. */
export function worstStatus(requirements: readonly Pick<TaxSetupRequirement, 'status'>[]): RequirementStatus {
  const applicable = requirements.filter((r) => r.status !== 'NotApplicable');
  if (applicable.length === 0) return 'NotApplicable';
  for (const s of STATUS_PRECEDENCE) if (applicable.some((r) => r.status === s)) return s;
  return 'NeedsReview';
}

/** "3 complete, 2 need attention". */
export function countsSentence(counts: { complete: number; needsAttention: number }): string {
  const attention = counts.needsAttention === 1 ? '1 needs attention' : `${counts.needsAttention} need attention`;
  return `${counts.complete} complete, ${attention}`;
}
